import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SingularStationArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { createEmptyAreas } from '@/modules/floorplanDesign/helpers/helpers';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';
import { useTranslatedOptions } from '@/modules/common/hooks';

type LoadHandlingTypeValue = NonNullable<SingularStationArea['loadHandlingType']>;

type Props = {
  selectedSingularStationAreas: SingularStationArea[];
};

const SINGULAR_STATION_LOAD_HANDLING_TYPE_OPTIONS: {
  labelKey: string;
  value: LoadHandlingTypeValue;
}[] = [
  { labelKey: 'interface:properties.area.load_handling_type_options.intake', value: 'INTAKE' },
  { labelKey: 'interface:properties.area.load_handling_type_options.delivery', value: 'DELIVERY' },
  { labelKey: 'interface:properties.area.load_handling_type_options.storage', value: 'STORAGE' },
  {
    labelKey: 'interface:properties.area.load_handling_type_options.off_vehicle_processing',
    value: 'OFF_VEHICLE_PROCESSING',
  },
  {
    labelKey: 'interface:properties.area.load_handling_type_options.on_vehicle_processing',
    value: 'ON_VEHICLE_PROCESSING',
  },
];

function SingularStationLoadHandlingTypeComponent({ selectedSingularStationAreas }: Props) {
  const { t } = useTranslation('interface');
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const options = useTranslatedOptions(SINGULAR_STATION_LOAD_HANDLING_TYPE_OPTIONS);

  // null shows the 'Mixed' placeholder when the areas have different load handling types
  const value = useMemo(
    () => getCommonOption(selectedSingularStationAreas.map((area) => area.loadHandlingType)),
    [selectedSingularStationAreas],
  );

  const onChange = useCallback(
    async (newValue: LoadHandlingTypeValue) => {
      const updatedAreas = selectedSingularStationAreas.filter(
        ({ loadHandlingType }) => loadHandlingType !== newValue,
      );
      if (updatedAreas.length === 0) return;

      const selectedIds = [...useAppStore.getState().selectedIds];
      if (updatedAreas.some(({ id }) => !selectedIds.includes(id))) return;

      const flows = updatedAreas.flatMap(({ id }) => fddmSvc.getFlowsByAreaId(id) ?? []);
      const flowStops = updatedAreas.flatMap(({ id }) => {
        const flowStop = fddmSvc.getFlowStopByAreaId(id);
        return flowStop ? [flowStop] : [];
      });
      const uniqueFlows = [
        ...new Map(flows.map((flow): [string, typeof flow] => [flow.id, flow])).values(),
      ];
      const uniqueFlowStops = [
        ...new Map(
          flowStops.map((flowStop): [string, typeof flowStop] => [flowStop.id, flowStop]),
        ).values(),
      ];

      if (uniqueFlows.length > 0 || uniqueFlowStops.length > 0) {
        const confirmed = await confirm(
          t('interface:properties.flows.change_load_handling_type.label'),
          t('interface:properties.flows.change_load_handling_type.message'),
        );
        if (!confirmed) return;
      }

      const currentIds = useAppStore.getState().selectedIds;
      if (
        currentIds.length !== selectedIds.length ||
        selectedIds.some((id, index) => id !== currentIds[index])
      ) {
        return;
      }

      const currentAreas = useFloorplanDesignStore
        .getState()
        .getAreasByIds(updatedAreas.map(({ id }) => id)).singularStationAreas;
      if (currentAreas.length !== updatedAreas.length) return;

      const currentAreasToUpdate = currentAreas.filter(
        ({ loadHandlingType }) => loadHandlingType !== newValue,
      );
      if (currentAreasToUpdate.length === 0) return;

      historySvc.batch(() => {
        if (uniqueFlowStops.length > 0) fddmSvc.deleteFlowStops(uniqueFlowStops);
        if (uniqueFlows.length > 0) fddmSvc.deleteFlows(uniqueFlows);

        fddmSvc.updateAreas({
          ...createEmptyAreas(),
          singularStationAreas: currentAreasToUpdate.map((area) => {
            const { loadHandlingType, loadHandlingPoiSpecs } = area;
            const resetCanBeUsedForParking = newValue !== 'INTAKE' && loadHandlingType === 'INTAKE';

            return {
              ...area,
              loadHandlingType: newValue,
              loadHandlingPoiSpecs: loadHandlingPoiSpecs.map((loadHandlingPoiSpec) => ({
                ...loadHandlingPoiSpec,
                canBeUsedForParking: resetCanBeUsedForParking
                  ? false
                  : loadHandlingPoiSpec.canBeUsedForParking,
                parkingExtension: resetCanBeUsedForParking
                  ? undefined
                  : loadHandlingPoiSpec.parkingExtension,
              })),
            };
          }),
        });
      });
    },
    [selectedSingularStationAreas, confirm, fddmSvc, historySvc, t],
  );

  if (selectedSingularStationAreas.length === 0) return null;

  return (
    <>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.load_handling_type'>
          <SelectInput options={options} onChange={onChange} value={value} showMixed />
        </PropertiesLabel>
      </div>
    </>
  );
}

export const LoadHandlingType = memo(SingularStationLoadHandlingTypeComponent);
